import { i18n } from "@/boot/i18n";
import DashboardService from "@/services/dashboard";
import ErrorHandler from "@/tools/errorHandler";
import { MAX_LENGTH, MIN_LENGTH, REQUIRED } from "../validator/methods";
import { CHARTS, CHART_TYPE, PERIOD } from "@/consts/dashboard";
import { DATE_RANGE } from "@/consts/dates";
import * as types from "./mutationTypes";

export default {
  initState({ commit, dispatch }) {
    commit(types.SET_DASHBOARDS, []);
    commit(types.SET_DASHBOARD, {});
    const validator = dashboardValidators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
  },
  clearState({ commit, dispatch }) {
    commit(types.CLEAR_DASHBOARDS);
    commit(types.CLEAR_DASHBOARD);
    dispatch("survey/clearSurveys", null, { root: true });
    dispatch("validator/clearValidator", null, { root: true });
  },
  setDashboardsSuccess({ commit }, dashboards) {
    commit(types.SET_DASHBOARDS, dashboards);
  },
  setDashboardSuccess({ commit, dispatch }, dashboard) {
    commit(types.SET_DASHBOARD, dashboard);
    if (dashboard.charts) {
      dashboard.charts.forEach((chart) => {
        dispatch("addChartValidator", chart.uuid);
      });
    }
  },
  async getAll({ rootState, dispatch }) {
    try {
      const response = await DashboardService.getAll(rootState.organization.organization.uuid);
      if (response.data.data) {
        dispatch("setDashboardsSuccess", response.data.data);
      }
    } catch (error) {
      if (error?.response?.status !== 401) {
        dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      }
    }
  },
  async getOne({ rootState, dispatch }) {
    try {
      const response = await DashboardService.getOne(
        rootState.organization.organization.uuid,
        rootState.organization.organization.user_dashboard
      );
      if (response.data.data) {
        await dispatch("setDashboardSuccess", response.data.data);
        dispatch("getChartsData");
      }
    } catch (error) {
      if (error?.response?.status !== 401) {
        dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      }
    }
  },
  async getLive({ dispatch }, hash) {
    try {
      const response = await DashboardService.getLive(hash);
      if (response.data.data) {
        await dispatch("setDashboardSuccess", response.data.data);
        dispatch("getChartsData", hash);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async switch({ rootState, dispatch }, dashboardUUID) {
    try {
      const response = await DashboardService.switch(rootState.organization.organization.uuid, dashboardUUID);
      if (response.data.data) {
        dispatch("organization/selectOrganization", response.data.data, { root: true });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async create({ rootState, dispatch }) {
    try {
      const dashboard = {
        name: i18n.t("dashboard.library.default_dashboard_name"),
        charts: []
      };
      const response = await DashboardService.create(rootState.organization.organization.uuid, dashboard);
      if (response.data.data) {
        await dispatch("switch", response.data.data.uuid);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async update({ rootState, dispatch, state }) {
    const valid = await dispatch("validateDashboard");
    if (!valid) {
      return;
    }
    try {
      await DashboardService.update(rootState.organization.organization.uuid, state.dashboard);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async delete({ rootState, dispatch, commit }, dashboardUUID) {
    try {
      const response = await DashboardService.delete(rootState.organization.organization.uuid, dashboardUUID);
      if (response.data.data) {
        dispatch("organization/setUserDashboard", response.data.data[0].uuid, { root: true });
      }
      commit(types.REMOVE_DASHBOARD, dashboardUUID);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async duplicate({ rootState, dispatch, commit }, dashboard) {
    try {
      const newDash = { ...dashboard };
      newDash.name = i18n.t("dashboard.library.copy", [newDash.name]);
      const response = await DashboardService.duplicate(rootState.organization.organization.uuid, newDash);
      if (response.data.data) {
        commit(types.ADD_DASHBOARD, response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async createChart({ rootState, dispatch, state, commit }, { type, metric }) {
    try {
      let initialPositionGridLayout = { x: 0, y: 0, w: 7, h: 30 }; // Initial position in grid layout
      if (CHARTS[type].LAYOUT) {
        initialPositionGridLayout = CHARTS[type].LAYOUT;
      }
      let chart = chartObject(type, metric);
      const y = getNextYPosition(state.dashboard);
      chart.layout = { ...initialPositionGridLayout };
      chart.layout.y = y;

      const dashboardUUID = state.dashboard.uuid;
      const response = await DashboardService.createChart(
        rootState.organization.organization.uuid,
        chart,
        dashboardUUID
      );
      if (response.data.data) {
        commit(types.ADD_CHART, response.data.data);
        dispatch("addChartValidator", response.data.data.uuid);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async updateChart({ rootState, dispatch, state, commit }, { chartUUID, onlyLayout }) {
    const chart = state.dashboard.charts.find((item) => item.uuid === chartUUID);
    /**
     * NOTE:
     * Pega o mesmo objeto de layout usando pelo componente Charts.vue
     * para garantir que os gráficos não iram mudar de posição depois de qualquer update
     **/
    const { layout } = state.layouts.find((item) => item.chartUUID === chartUUID);
    if (!chart) return;

    try {
      const dashboardUUID = state.dashboard.uuid;
      const response = await DashboardService.updateChart(
        rootState.organization.organization.uuid,
        { ...chart, layout },
        dashboardUUID,
        onlyLayout
      );
      if (response.data.data && !onlyLayout) {
        commit(types.UPDATE_CHART, response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async updateChartLayout({ rootState, dispatch, state, commit }, { chart }) {
    try {
      const dashboardUUID = state.dashboard.uuid;
      const organizationUUID = rootState.organization.organization.uuid;
      await DashboardService.updateChart(organizationUUID, chart, dashboardUUID, true);
      commit(types.UPDATE_CHART, chart);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  updateDashboardChartLayout({ commit }, { layouts }) {
    commit(types.UPDATE_CHART_LAYOUT, { layouts });
  },
  async deleteChart({ rootState, state, dispatch, commit }, chartUUID) {
    try {
      const dashboardUUID = state.dashboard.uuid;
      await DashboardService.deleteChart(rootState.organization.organization.uuid, chartUUID, dashboardUUID);
      commit(types.REMOVE_CHART, chartUUID);
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOneChart({ rootState, state, dispatch, commit }, { chartUUID, pagination }) {
    try {
      if (!state.dashboard) return;
      const response = await DashboardService.getOneChart(
        rootState.organization.organization.uuid,
        state.dashboard.uuid,
        chartUUID,
        pagination
      );
      if (response.data.data) {
        commit(types.UPDATE_CHART, response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOneChartByDashboardHash({ dispatch, commit }, { chartUUID, dashHash }) {
    try {
      const response = await DashboardService.getOneChartByDashboardHash(dashHash, chartUUID);
      if (response.data.data) {
        commit(types.UPDATE_CHART, response.data.data);
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getChartsData({ state, dispatch }, liveDashboardHash) {
    /*
    Descomente para utilizar cache nos gráficos do dashboard

    const chartsOnSession = await dataSession.getFromSessionStorage(state.dashboard.uuid);
    if (chartsOnSession) {
      commit(types.SET_CHARTS, chartsOnSession);
      return;
    }
    */
    const charts = [...state.dashboard.charts];
    charts.sort(compareChart);
    for (const chart of charts) {
      if (liveDashboardHash) {
        await dispatch("getOneChartByDashboardHash", { chartUUID: chart.uuid, dashHash: liveDashboardHash });
      } else {
        await dispatch("getOneChart", { chartUUID: chart.uuid });
      }
    }
    /*
    dataSession.setOnSessionStorage(state.dashboard.uuid, state.dashboard.charts);
    */
  },
  setName({ commit }, name) {
    commit(types.SET_NAME, name);
  },
  setPeriod({ commit, dispatch }, { chartUUID, period }) {
    commit(types.SET_PERIOD, { chartUUID, period });
    dispatch("updateChart", { chartUUID });
  },
  setCompareSurveys({ commit, dispatch }, { chartUUID, isCompareSurveys }) {
    commit(types.SET_COMPARE_SURVEYS, { chartUUID, isCompareSurveys });
    dispatch("updateChart", { chartUUID });
  },
  setGeneralMetric({ commit, dispatch }, { chartUUID, generalMetric }) {
    commit(types.SET_GENERAL_METRIC, { chartUUID, generalMetric });
    dispatch("updateChart", { chartUUID });
  },
  setMetrics({ commit }, { chartUUID, metrics }) {
    let metricSet = metrics;
    if (!Array.isArray(metrics)) {
      metricSet = [metrics];
    }
    commit(types.SET_METRICS, { chartUUID, metrics: metricSet });
  },
  addMetric({ commit }, { chartUUID, metric }) {
    commit(types.ADD_METRIC, { chartUUID, metric });
  },
  setSurveys({ commit }, { chartUUID, surveys }) {
    const surveysFinal = surveys.map((option) => {
      let selectedItemsUUIDs = [];
      if (option.items) {
        selectedItemsUUIDs = option.items.map((surveyItem) => surveyItem.item.uuid);
      }
      return {
        uuid: option.uuid,
        name: option.name,
        selected_items: selectedItemsUUIDs
      };
    });
    commit(types.SET_SURVEYS, { chartUUID, surveys: surveysFinal });
  },
  setSurvey({ commit }, { chartUUID, surveys, checkedItem }) {
    const surveysFinal = surveys.map((option) => {
      return {
        uuid: option.uuid,
        name: option.name,
        selected_items: checkedItem ? [checkedItem] : []
      };
    });
    commit(types.SET_SURVEYS, { chartUUID, surveys: surveysFinal });
  },
  removeSurvey({ commit }, { chartUUID, surveyUUID }) {
    commit(types.REMOVE_SURVEY, { chartUUID, surveyUUID });
  },
  setCompositeMetrics({ commit, dispatch }, { chartUUID, showCompositeMetrics }) {
    commit(types.SET_COMPOSITE_METRICS, { chartUUID, showCompositeMetrics });
    dispatch("updateChart", { chartUUID });
  },
  addSelectedItem({ commit }, { chartUUID, surveyUUID, itemUUID }) {
    commit(types.ADD_SELECTED_ITEM, { chartUUID, surveyUUID, itemUUID });
  },
  removeSelectedItem({ commit }, { chartUUID, surveyUUID, itemUUID }) {
    commit(types.REMOVE_SELECTED_ITEM, { chartUUID, surveyUUID, itemUUID });
  },
  setFilterDates({ commit, dispatch }, { chartUUID, startDate, endDate, range }) {
    commit(types.SET_FILTER_DATES, { chartUUID, startDate, endDate, range });
    dispatch("updateChart", { chartUUID });
  },
  setFilterChannel({ commit, dispatch }, { chartUUID, channel }) {
    commit(types.SET_FILTER_CHANNEL, { chartUUID, channel });
    dispatch("updateChart", { chartUUID });
  },
  removeFilterChannel({ commit, dispatch }, { chartUUID, channel }) {
    commit(types.REMOVE_FILTER_CHANNEL, { chartUUID, channel });
    dispatch("updateChart", { chartUUID });
  },
  setFilterMetadata({ commit, dispatch }, { chartUUID, metadata }) {
    commit(types.SET_FILTER_METADATA, { chartUUID, metadata });
    dispatch("updateChart", { chartUUID });
  },
  setFilterAnswerStatus({ commit, dispatch }, { chartUUID, status }) {
    commit(types.SET_FILTER_ANSWER_STATUS, { chartUUID, status });
    dispatch("updateChart", { chartUUID });
  },
  removeFilterMetadata({ commit, dispatch }, { chartUUID, metadata }) {
    commit(types.SET_FILTER_METADATA, { chartUUID, metadata });
    dispatch("updateChart", { chartUUID });
  },
  setDesign({ commit }, { chartUUID, design }) {
    commit(types.SET_DESIGN, { chartUUID, design });
  },
  setChartComponent({ commit }, { chartUUID, component }) {
    commit(types.SET_CHART_COMPONENT, { chartUUID, component });
  },
  async setChartName({ commit, dispatch }, { chartUUID, name }) {
    const valid = await dispatch("validator/validate", { field: chartUUID, value: name }, { root: true });
    if (valid) {
      commit(types.SET_CHART_NAME, { chartUUID, name });
      dispatch("updateChart", { chartUUID });
    }
  },
  setLayout({ commit }, { layouts }) {
    commit(types.SET_LAYOUT, { layouts });
  },
  validateDashboard({ rootState, state, dispatch }) {
    dispatch("validator/validate", { field: "name", value: state.dashboard.name }, { root: true });
    return rootState.validator.errors.length === 0;
  },
  addChartValidator({ dispatch }, chartUUID) {
    const chartValidators = {
      nameValidator: {
        field: chartUUID,
        validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }]
      }
    };
    dispatch("validator/addValidator", chartValidators.nameValidator, { root: true });
  }
};

const dashboardValidators = () => {
  return {
    nameValidator: {
      field: "name",
      validations: [
        { method: REQUIRED, message: i18n.t("general.rules.required") },
        { method: MIN_LENGTH, value: 4, message: i18n.t("general.rules.min_length") },
        { method: MAX_LENGTH, value: 50, message: i18n.t("general.rules.max_length") }
      ]
    }
  };
};

function chartObject(type, metric) {
  if (type === CHART_TYPE.NPS) {
    return {
      name: i18n.t(CHARTS[type].DEFAULT_NAME),
      type,
      settings: {
        period: null,
        metrics: [metric],
        filter: {
          start_date: null,
          end_date: null,
          range: DATE_RANGE.LAST_30_DAYS
        }
      }
    };
  }
  if (type === CHART_TYPE.EVOLUTION || type === CHART_TYPE.DISTRIBUTION) {
    return {
      name: i18n.t(CHARTS[type].DEFAULT_NAME),
      type,
      settings: {
        period: PERIOD.DAILY7,
        metrics: [metric],
        is_compare_surveys: false,
        show_general_metric: false,
        filter: {
          start_date: null,
          end_date: null,
          range: DATE_RANGE.LAST_30_DAYS
        }
      }
    };
  }

  return {
    name: i18n.t(CHARTS[type].DEFAULT_NAME),
    type,
    settings: {
      period: null,
      metrics: [metric],
      filter: {
        start_date: null,
        end_date: null,
        range: DATE_RANGE.LAST_30_DAYS
      }
    }
  };
}

function getNextYPosition(dashboard) {
  if (!dashboard.charts || dashboard.charts.length === 0) {
    return 0;
  }
  const maxY = Math.max.apply(
    Math,
    dashboard.charts.map(function (chart) {
      return chart.layout.y;
    })
  );
  return maxY + 30;
}

function compareChart(chart1, chart2) {
  if (chart1.layout.y < chart2.layout.y) {
    return -1;
  }
  if (chart1.layout.y > chart2.layout.y) {
    return 1;
  }
  return 0;
}
